import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiSave, FiArrowLeft, FiTrash2 } from "react-icons/fi";
import { LuRuler } from "react-icons/lu";
import AdminContainer from "../../../components/utils/AdminContainer";
import { toast } from "../../../components/modern-ui/sonner";
import {
  PageSection,
  PageHeader,
  PageTitle,
  Button,
} from "../AdminProductsPage/AdminProductsPage.styles";
import {
  getProduct,
  getProductDimensions,
  saveProductDimensions,
  deleteProductDimensions,
} from "../../../store/slices/products/asyncThunks";
import {
  selectSelectedProduct,
  selectProductDimensions,
} from "../../../store/slices/products/slice";
import styled, { keyframes } from "styled-components";

const ProductDimensionsPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const selectedProduct = useSelector(selectSelectedProduct);
  const productDimensions = useSelector(selectProductDimensions);

  const product = selectedProduct.data;
  const loading = selectedProduct.loading || productDimensions.loading;
  const saving =
    productDimensions.saveLoading || productDimensions.deleteLoading;
  const [dimensions, setDimensions] = useState({
    chestS: "",
    lengthS: "",
    sleeveS: "",
    chestM: "",
    lengthM: "",
    sleeveM: "",
    chestL: "",
    lengthL: "",
    sleeveL: "",
    chestXL: "",
    lengthXL: "",
    sleeveXL: "",
    chestXXL: "",
    lengthXXL: "",
    sleeveXXL: "",
    chest3XL: "",
    length3XL: "",
    sleeve3XL: "",
    chest4XL: "",
    length4XL: "",
    sleeve4XL: "",
    chest5XL: "",
    length5XL: "",
    sleeve5XL: "",
  });

  const sizes = [
    { key: "S", label: "S" },
    { key: "M", label: "M" },
    { key: "L", label: "L" },
    { key: "XL", label: "XL" },
    { key: "XXL", label: "XXL" },
    { key: "3XL", label: "3XL" },
    { key: "4XL", label: "4XL" },
    { key: "5XL", label: "5XL" },
  ];

  // Učitaj proizvod i postojeće dimenzije
  useEffect(() => {
    if (productId) {
      dispatch(getProduct(productId));
      dispatch(getProductDimensions(productId));
    }
  }, [dispatch, productId]);

  // Update dimensions state when product dimensions are loaded
  useEffect(() => {
    if (productDimensions.data) {
      setDimensions({
        chestS: productDimensions.data.chestS || "",
        lengthS: productDimensions.data.lengthS || "",
        sleeveS: productDimensions.data.sleeveS || "",
        chestM: productDimensions.data.chestM || "",
        lengthM: productDimensions.data.lengthM || "",
        sleeveM: productDimensions.data.sleeveM || "",
        chestL: productDimensions.data.chestL || "",
        lengthL: productDimensions.data.lengthL || "",
        sleeveL: productDimensions.data.sleeveL || "",
        chestXL: productDimensions.data.chestXL || "",
        lengthXL: productDimensions.data.lengthXL || "",
        sleeveXL: productDimensions.data.sleeveXL || "",
        chestXXL: productDimensions.data.chestXXL || "",
        lengthXXL: productDimensions.data.lengthXXL || "",
        sleeveXXL: productDimensions.data.sleeveXXL || "",
        chest3XL: productDimensions.data.chest3XL || "",
        length3XL: productDimensions.data.length3XL || "",
        sleeve3XL: productDimensions.data.sleeve3XL || "",
        chest4XL: productDimensions.data.chest4XL || "",
        length4XL: productDimensions.data.length4XL || "",
        sleeve4XL: productDimensions.data.sleeve4XL || "",
        chest5XL: productDimensions.data.chest5XL || "",
        length5XL: productDimensions.data.length5XL || "",
        sleeve5XL: productDimensions.data.sleeve5XL || "",
      });
    }
  }, [productDimensions.data]);

  const handleInputChange = (field, value) => {
    setDimensions((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    // Filtriraj prazne vrednosti
    const cleanedDimensions = {};
    Object.entries(dimensions).forEach(([key, value]) => {
      if (value && value.toString().trim() !== "") {
        cleanedDimensions[key] = parseFloat(value) || 0;
      }
    });

    dispatch(
      saveProductDimensions({ productId, dimensionsData: cleanedDimensions })
    )
      .unwrap()
      .then(() => {
        toast.success("Dimenzije su uspešno sačuvane!");
      })
      .catch((err) => {
        toast.error(err.message || "Greška pri čuvanju dimenzija");
      });
  };

  const handleDelete = () => {
    if (
      !window.confirm(
        "Da li ste sigurni da želite da obrišete sve dimenzije za ovaj proizvod?"
      )
    ) {
      return;
    }

    dispatch(deleteProductDimensions(productId))
      .unwrap()
      .then(() => {
        toast.success("Dimenzije su uspešno obrisane!");
        // Resetuj dimenzije
        setDimensions({
          chestS: "",
          lengthS: "",
          sleeveS: "",
          chestM: "",
          lengthM: "",
          sleeveM: "",
          chestL: "",
          lengthL: "",
          sleeveL: "",
          chestXL: "",
          lengthXL: "",
          sleeveXL: "",
          chestXXL: "",
          lengthXXL: "",
          sleeveXXL: "",
          chest3XL: "",
          length3XL: "",
          sleeve3XL: "",
          chest4XL: "",
          length4XL: "",
          sleeve4XL: "",
          chest5XL: "",
          length5XL: "",
          sleeve5XL: "",
        });
      })
      .catch((err) => {
        toast.error(err.message || "Greška pri brisanju dimenzija");
      });
  };

  if (loading) {
    return (
      <AdminContainer>
        <StyledPageSection>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "50vh",
              color: "#ff6b35",
              fontSize: "1.2rem",
              fontWeight: "600",
            }}
          >
            <LuRuler
              style={{
                marginRight: "0.5rem",
                animation: "spin 2s linear infinite",
              }}
            />
            Učitavam dimenzije...
          </div>
        </StyledPageSection>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <StyledPageSection>
        <StyledPageHeader>
          <HeaderContent>
            <BackButton onClick={() => navigate("/admin-panel/proizvodi")}>
              <FiArrowLeft />
              Nazad na proizvode
            </BackButton>
            <StyledPageTitle>
              Dimenzije proizvoda: {product?.name || "Nepoznat proizvod"}
            </StyledPageTitle>
            <PageDescription>
              Unesite dimenzije u centimetrima za svaku dostupnu veličinu
            </PageDescription>
          </HeaderContent>
          <HeaderActions>
            <Button
              $variant="danger"
              onClick={handleDelete}
              disabled={saving}
              style={{ padding: 10, minHeight: "unset" }}
            >
              <FiTrash2 />
            </Button>
            <Button
              $variant="primary"
              onClick={handleSave}
              disabled={saving}
              style={{ padding: 10, minHeight: "unset" }}
            >
              <FiSave />
            </Button>
          </HeaderActions>
        </StyledPageHeader>

        <DimensionsGrid>
          {sizes.map((size) => (
            <SizeCard key={size.key}>
              <SizeHeader>
                <SizeTitle>{size.label}</SizeTitle>
              </SizeHeader>

              <DimensionRow>
                <DimensionLabel>Širina grudi (cm)</DimensionLabel>
                <DimensionInput
                  type="number"
                  step="0.1"
                  min="0"
                  value={dimensions[`chest${size.key}`]}
                  onChange={(e) =>
                    handleInputChange(`chest${size.key}`, e.target.value)
                  }
                  placeholder="npr. 46.5"
                />
              </DimensionRow>

              <DimensionRow>
                <DimensionLabel>Dužina (cm)</DimensionLabel>
                <DimensionInput
                  type="number"
                  step="0.1"
                  min="0"
                  value={dimensions[`length${size.key}`]}
                  onChange={(e) =>
                    handleInputChange(`length${size.key}`, e.target.value)
                  }
                  placeholder="npr. 66.0"
                />
              </DimensionRow>

              <DimensionRow>
                <DimensionLabel>Dužina rukava (cm)</DimensionLabel>
                <DimensionInput
                  type="number"
                  step="0.1"
                  min="0"
                  value={dimensions[`sleeve${size.key}`]}
                  onChange={(e) =>
                    handleInputChange(`sleeve${size.key}`, e.target.value)
                  }
                  placeholder="npr. 19.5"
                />
              </DimensionRow>
            </SizeCard>
          ))}
        </DimensionsGrid>

        <InfoSection>
          <InfoTitle>Napomene:</InfoTitle>
          <InfoList>
            <InfoItem>Sve dimenzije su u centimetrima</InfoItem>
            <InfoItem>
              Možete koristiti decimalne vrednosti (npr. 46.5)
            </InfoItem>
            <InfoItem>
              Ostavite prazno ako dimenzija nije dostupna za tu veličinu
            </InfoItem>
            <InfoItem>
              Dimenzije će se prikazati u vodiču za veličine na stranici
              proizvoda
            </InfoItem>
          </InfoList>
        </InfoSection>
      </StyledPageSection>
    </AdminContainer>
  );
};

export default ProductDimensionsPage;

// Styled Components with AdminProductsPage theme

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #ff6b35;
  cursor: pointer;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  padding: 0.5rem 0;
  transition: all 0.3s ease;
  font-weight: 500;

  &:hover {
    color: #e55a2b;
    transform: translateX(-3px);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(-2px);
  }
`;

const PageDescription = styled.p`
  margin: 0;
  color: #cccccc;
  font-size: 0.95rem;
  opacity: 0.8;
`;

const DimensionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
  animation: ${fadeIn} 0.6s ease-out;
`;

const SizeCard = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
  border: 1px solid #444;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #ff6b35, #e55a2b);
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(255, 107, 53, 0.2);
    border-color: #ff6b35;
  }
`;

const SizeHeader = styled.div`
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid rgba(255, 107, 53, 0.3);
  text-align: center;
`;

const SizeTitle = styled.h3`
  margin: 0;
  color: #ff6b35;
  font-size: 1.4rem;
  font-weight: 700;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const DimensionRow = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DimensionLabel = styled.label`
  display: block;
  margin-bottom: 0.7rem;
  color: #ffffff;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: "•";
    color: #ff6b35;
    font-weight: bold;
    font-size: 1.2rem;
  }
`;

const DimensionInput = styled.input`
  width: 100%;
  padding: 1rem;
  border: 2px solid #444;
  border-radius: 10px;
  font-size: 1rem;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  transition: all 0.3s ease;
  font-weight: 500;

  &:focus {
    outline: none;
    border-color: #ff6b35;
    box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.2);
    background: rgba(255, 255, 255, 0.08);
  }

  &:hover {
    border-color: #666;
  }

  &::placeholder {
    color: #888;
    font-style: italic;
  }
`;

const InfoSection = styled.div`
  background: linear-gradient(
    135deg,
    rgba(255, 107, 53, 0.1) 0%,
    rgba(26, 26, 26, 0.8) 100%
  );
  border-radius: 15px;
  padding: 2rem;
  border: 2px solid rgba(255, 107, 53, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #ff6b35, #e55a2b, #ff6b35);
  }
`;

const InfoTitle = styled.h4`
  margin: 0 0 1.5rem 0;
  color: #ff6b35;
  font-weight: 700;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InfoList = styled.ul`
  margin: 0;
  padding-left: 2rem;
  color: #cccccc;
`;

const InfoItem = styled.li`
  margin-bottom: 0.8rem;
  font-size: 0.95rem;
  line-height: 1.5;
  transition: all 0.2s ease;

  &:hover {
    color: #ffffff;
    transform: translateX(5px);
  }

  &:last-child {
    margin-bottom: 0;
  }

  &::marker {
    color: #ff6b35;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: flex-end;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StyledPageSection = styled(PageSection)`
  padding: 2rem;
  min-height: 100vh;
  color: #ffffff;
`;

const StyledPageHeader = styled(PageHeader)`
  background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
  border-radius: 15px;
  padding: 2rem;
  border: 1px solid #444;
  margin-bottom: 3rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #ff6b35, #e55a2b);
  }
`;

const StyledPageTitle = styled(PageTitle)`
  color: #ffffff;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.7rem;
`;
